import { useEffect, useState } from "react";
import { Plus, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../api.js";

function fmt(date) {
  return date.toISOString().slice(0, 10);
}

function buildMonthGrid(year, month) {
  // month: 0-11. Woche beginnt Montag.
  const first = new Date(year, month, 1);
  const startOffset = (first.getDay() + 6) % 7; // 0 = Montag
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  return cells;
}

export default function SocialScreen() {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [cursor, setCursor] = useState(() => {
    const n = new Date();
    return { year: n.getFullYear(), month: n.getMonth() };
  });
  const [dragId, setDragId] = useState(null);

  const load = () => api.list("social-items").then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!title) return;
    await api.createItem("social-items", { title, note });
    setTitle(""); setNote("");
    load();
  };

  const toggleDone = (item) => api.updateItem("social-items", item.id, { done: !item.done }).then(load);
  const setDate = (item, date) => api.updateItem("social-items", item.id, { date }).then(load);

  const open = items.filter((i) => !i.done);
  const done = items.filter((i) => i.done);
  const backlog = open.filter((i) => !i.date);

  const cells = buildMonthGrid(cursor.year, cursor.month);
  const monthLabel = new Date(cursor.year, cursor.month, 1).toLocaleDateString("de-DE", { month: "long", year: "numeric" });

  const onDrop = (date) => (e) => {
    e.preventDefault();
    const id = Number(e.dataTransfer.getData("text/plain"));
    const item = items.find((i) => i.id === id);
    if (item) setDate(item, date ? fmt(date) : null);
  };

  return (
    <div>
      <form className="dx-inline-form" onSubmit={add} style={{ marginBottom: "20px" }}>
        <input className="dx-input" placeholder="Titel" value={title} onChange={(e) => setTitle(e.target.value)} />
        <input className="dx-input" placeholder="Notiz" value={note} onChange={(e) => setNote(e.target.value)} />
        <button className="dx-btn dx-btn--primary" type="submit"><Plus size={16} /> Hinzufügen</button>
      </form>

      {/* ---- Desktop: Kalender + Backlog ---- */}
      <div className="dx-only-desktop">
        <div className="dx-social-desktop-grid">
          <div className="dx-card">
            <div className="dx-calendar-header">
              <button className="dx-btn dx-btn--ghost" onClick={() => setCursor((c) => c.month === 0 ? { year: c.year - 1, month: 11 } : { year: c.year, month: c.month - 1 })}><ChevronLeft size={16} /></button>
              <strong style={{ textTransform: "capitalize" }}>{monthLabel}</strong>
              <button className="dx-btn dx-btn--ghost" onClick={() => setCursor((c) => c.month === 11 ? { year: c.year + 1, month: 0 } : { year: c.year, month: c.month + 1 })}><ChevronRight size={16} /></button>
            </div>
            <div className="dx-calendar-grid">
              {["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"].map((d) => <div key={d} className="dx-calendar-weekday">{d}</div>)}
              {cells.map((date, i) => (
                <div
                  key={i}
                  className="dx-calendar-cell"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={date ? onDrop(date) : undefined}
                >
                  {date ? (
                    <>
                      <div className="dx-calendar-daynum">{date.getDate()}</div>
                      {open.filter((it) => it.date && it.date.slice(0, 10) === fmt(date)).map((it) => (
                        <div
                          key={it.id}
                          className="dx-calendar-item"
                          draggable
                          onDragStart={(e) => e.dataTransfer.setData("text/plain", String(it.id))}
                          onClick={() => toggleDone(it)}
                          title="Klicken = erledigt markieren"
                        >
                          {it.title}
                        </div>
                      ))}
                    </>
                  ) : null}
                </div>
              ))}
            </div>
          </div>

          <div className="dx-card">
            <strong>Noch nicht geplant</strong>
            <div className="dx-backlog" onDragOver={(e) => e.preventDefault()} onDrop={onDrop(null)}>
              {backlog.map((it) => (
                <div
                  key={it.id}
                  className="dx-calendar-item dx-calendar-item--backlog"
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData("text/plain", String(it.id))}
                >
                  <strong>{it.title}</strong>
                  {it.note ? <div className="dx-muted" style={{ fontSize: "12px" }}>{it.note}</div> : null}
                </div>
              ))}
              {backlog.length === 0 ? <p className="dx-muted" style={{ fontSize: "13px" }}>Alles verplant.</p> : null}
            </div>
          </div>
        </div>
      </div>

      {/* ---- Mobil: einfache Agenda-Liste ---- */}
      <div className="dx-only-mobile">
        <h3>Geplant</h3>
        <div className="dx-list">
          {open.filter((i) => i.date).sort((a, b) => a.date.localeCompare(b.date)).map((i) => (
            <div key={i.id} className="dx-card dx-social-item">
              <div>
                <strong>{i.title}</strong>
                <div className="dx-muted" style={{ fontSize: "12.5px" }}>{new Date(i.date).toLocaleDateString("de-DE")}</div>
              </div>
              <button className="dx-btn dx-btn--primary" onClick={() => toggleDone(i)}>Erledigt</button>
            </div>
          ))}
        </div>
        <h3 style={{ marginTop: "20px" }}>Noch nicht geplant</h3>
        <div className="dx-list">
          {backlog.map((i) => (
            <div key={i.id} className="dx-card dx-social-item">
              <div>
                <strong>{i.title}</strong>
                {i.note ? <div className="dx-muted" style={{ fontSize: "12.5px" }}>{i.note}</div> : null}
              </div>
              <input type="date" className="dx-input" onChange={(e) => setDate(i, e.target.value)} />
            </div>
          ))}
        </div>
      </div>

      <h3 style={{ marginTop: "24px" }}>Erledigt</h3>
      <div className="dx-list">
        {done.map((i) => (
          <div key={i.id} className="dx-card dx-social-item dx-social-item--done">
            <strong>{i.title}</strong>
            <button className="dx-btn dx-btn--ghost" onClick={() => toggleDone(i)}>Zurücksetzen</button>
          </div>
        ))}
      </div>
    </div>
  );
}
